import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import AppDialog from "@/components/common/AppDialog";
import { PATHS } from "@/lib/router";
import { showToast } from "@/lib/utils/toast";
import { useAuthStore } from "@/features/auth";
import SurveyList from "./SurveyList";
import SurveyShareDialog from "./SurveyShareDialog";
import {
  useSurveyShareStore,
  useSurveyTakeStore,
  useSurveysStore,
  useSurveysUiStore,
} from "../hooks";
import { surveysService } from "../services";

const CustomSurveysTab = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const { user, refreshMe } = useAuthStore();
  const { openTake } = useSurveyTakeStore();
  const shareStore = useSurveyShareStore();
  const surveysUiStore = useSurveysUiStore();
  const { customSurveys, customLoading, customError, loadCustomSurveys } =
    useSurveysStore();
  const {
    customDeleteTarget,
    customDeleteLoading,
    openCustomDelete,
    closeCustomDelete,
    setCustomDeleteLoading,
  } = surveysUiStore;
  const [companyRefreshing, setCompanyRefreshing] = useState(false);
  const companyId =
    user?.companyId ||
    user?.company?._id ||
    user?.company?.id ||
    user?.company?.companyId ||
    null;

  useEffect(() => {
    loadCustomSurveys();
  }, [loadCustomSurveys]);

  useEffect(() => {
    if (companyId || !refreshMe) return;
    let cancelled = false;
    const ensureCompany = async () => {
      setCompanyRefreshing(true);
      try {
        await refreshMe();
      } finally {
        if (!cancelled) setCompanyRefreshing(false);
      }
    };

    ensureCompany();
    return () => {
      cancelled = true;
    };
  }, [companyId, refreshMe]);

  const handlePublish = async (survey) => {
    if (!survey?._id) return;
    const { success } = await surveysService.publishCustomSurvey(survey._id);
    if (!success) {
      throw new Error(t("Unable to publish survey."));
    }
    showToast.success(t("Survey published."));
    loadCustomSurveys();
  };

  const handleArchive = async (survey) => {
    if (!survey?._id) return;
    const { success } = await surveysService.archiveCustomSurvey(survey._id);
    if (!success) {
      throw new Error(t("Unable to archive survey."));
    }
    showToast.success(t("Survey archived."));
    loadCustomSurveys();
  };

  const handleDelete = async () => {
    if (!customDeleteTarget?._id) return;
    setCustomDeleteLoading(true);
    try {
      const { success } = await surveysService.deleteCustomSurvey(
        customDeleteTarget._id,
      );
      if (!success) {
        throw new Error(t("Unable to delete survey."));
      }
      showToast.success(t("Survey deleted."));
      closeCustomDelete();
      loadCustomSurveys();
    } finally {
      setCustomDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-radius border border-border bg-card/60 p-4 shadow-sm">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            {t("Custom Surveys")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t(
              "Manage your custom question sets and launch them for respondents.",
            )}
          </p>
          {!companyId && !companyRefreshing ? (
            <p className="mt-2 text-xs font-medium text-primary">
              {t(
                "Add company information to your profile before creating custom surveys.",
              )}
            </p>
          ) : null}
        </div>
        <Button
          type="button"
          disabled={!companyId || companyRefreshing}
          onClick={() => navigate(PATHS.CUSTOM_SURVEY_NEW)}
        >
          {companyRefreshing ? t("Loading...") : t("Create Custom Survey")}
        </Button>
      </div>

      {customError ? (
        <p className="text-sm text-destructive">{customError}</p>
      ) : null}

      {customLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-32 w-full" />
          ))}
        </div>
      ) : (
        <SurveyList
          surveys={customSurveys}
          actionLabel={t("Take Survey")}
          secondaryActionLabel={t("View Details")}
          onSelect={(survey) => {
            if (survey?.status !== "published") return;
            openTake(survey);
          }}
          onSecondaryAction={(survey) =>
            navigate(
              PATHS.CUSTOM_SURVEY_EDIT.replace(":surveyId", survey?._id || ""),
            )
          }
          showPrimaryAction
          showShareButton
          onShare={(survey) => shareStore.openDialog(survey)}
          showQuestionCountBubble={false}
          showCategoryBadge={false}
          showResponseCount={false}
          canEditCustom
          onEditCustom={(survey) =>
            navigate(
              PATHS.CUSTOM_SURVEY_EDIT.replace(":surveyId", survey?._id || ""),
            )
          }
          canPublishCustom
          onPublishCustom={handlePublish}
          onArchiveCustom={handleArchive}
          onDeleteCustom={(survey) => openCustomDelete(survey)}
        />
      )}

      {customDeleteTarget ? (
        <AppDialog
          open
          onOpenChange={(open) => {
            if (!open) closeCustomDelete();
          }}
          title={t("Delete survey")}
          description={t("This will permanently delete the survey.")}
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={closeCustomDelete}>
                {t("Cancel")}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={customDeleteLoading}
              >
                {customDeleteLoading ? t("Deleting...") : t("Delete")}
              </Button>
            </div>
          }
        />
      ) : null}

      <SurveyShareDialog />
    </div>
  );
});

export default CustomSurveysTab;
